import { type ReactNode } from 'react';

/* ─── Panel ────────────────────────────────────────────────────
 * The standard card container for the warm-palette dashboard.
 * Wraps content in a white card with border and shadow.
 *
 * Usage:
 *   <Panel>
 *     <PanelHeader title="Section" />
 *     <PanelBody>content</PanelBody>
 *   </Panel>
 * ───────────────────────────────────────────────────────────── */

interface PanelProps {
  children: ReactNode;
  className?: string;
  id?: string;
}

export function Panel({ children, className = '', id }: PanelProps) {
  return (
    <div id={id} className={`bg-white rounded-lg border border-line shadow-warm-sm overflow-hidden ${className}`}>
      {children}
    </div>
  );
}

/* ─── PanelHeader ──────────────────────────────────────────────
 * Section header with coral accent bar.
 *
 *   <PanelHeader title="Environment Variables" />
 *   <PanelHeader title="Issues" subtitle="3 / 12 selected" action={<button>...</button>} />
 *   <PanelHeader title="Errors" accent="red" />
 * ───────────────────────────────────────────────────────────── */

type AccentColor = 'coral' | 'red' | 'accent' | 'amber' | 'neutral';

const accentBarColors: Record<AccentColor, string> = {
  coral: 'bg-coral-400',
  red: 'bg-red-400',
  accent: 'bg-accent-400',
  amber: 'bg-amber-400',
  neutral: 'bg-neutral-400',
};

interface PanelHeaderProps {
  title: string;
  subtitle?: string;
  accent?: AccentColor;
  action?: ReactNode;
}

export function PanelHeader({ title, subtitle, accent = 'coral', action }: PanelHeaderProps) {
  return (
    <div className="px-4 py-2.5 border-b border-line flex items-center justify-between">
      <h3 className="text-xs font-bold text-text uppercase tracking-widest flex items-center gap-2">
        <span className={`w-1 h-4 ${accentBarColors[accent]} rounded-full`} />
        {title}
        {subtitle && (
          <span className="text-subtle font-normal normal-case tracking-normal text-[11px]">
            {subtitle}
          </span>
        )}
      </h3>
      {action}
    </div>
  );
}

/* ─── PanelBody ────────────────────────────────────────────────
 * Standard padded body for panel content.
 * ───────────────────────────────────────────────────────────── */

interface PanelBodyProps {
  children: ReactNode;
  className?: string;
  spacing?: 'none' | 'sm' | 'md';
}

const spacingMap = {
  none: 'p-4',
  sm: 'p-4 space-y-2',
  md: 'p-4 space-y-3',
};

export function PanelBody({ children, className = '', spacing = 'none' }: PanelBodyProps) {
  return <div className={`${spacingMap[spacing]} ${className}`}>{children}</div>;
}

/* ─── FieldLabel ───────────────────────────────────────────────
 * Small uppercase label used for KPI values, read-only fields,
 * and metadata rows.
 *
 *   <FieldLabel>Version</FieldLabel>
 * ───────────────────────────────────────────────────────────── */

interface FieldLabelProps {
  children: ReactNode;
  className?: string;
}

export function FieldLabel({ children, className = '' }: FieldLabelProps) {
  return (
    <span className={`text-[9px] font-bold uppercase tracking-widest text-subtle ${className}`}>
      {children}
    </span>
  );
}

/* ─── StatusBadge ──────────────────────────────────────────────
 * Colored pill for status indicators.
 *
 *   <StatusBadge variant="success">Completed</StatusBadge>
 *   <StatusBadge variant="active">Running</StatusBadge>
 * ───────────────────────────────────────────────────────────── */

type BadgeVariant = 'active' | 'success' | 'error' | 'warning' | 'neutral' | 'info';

const badgeVariants: Record<BadgeVariant, string> = {
  active: 'bg-coral-100 text-coral-700',
  success: 'bg-accent-100 text-accent-700',
  error: 'bg-red-100 text-red-700',
  warning: 'bg-amber-100 text-amber-700',
  neutral: 'bg-surface-subtle text-muted',
  info: 'bg-coral-50 text-coral-600',
};

interface StatusBadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  className?: string;
}

export function StatusBadge({ children, variant = 'neutral', className = '' }: StatusBadgeProps) {
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${badgeVariants[variant]} ${className}`}>
      {children}
    </span>
  );
}

/* ─── DashedEmpty ──────────────────────────────────────────────
 * Dashed-border empty state for zones with no content.
 *
 *   <DashedEmpty icon={<Cpu className="w-6 h-6" />} message="No profiles" hint="Create one" />
 * ───────────────────────────────────────────────────────────── */

interface DashedEmptyProps {
  icon: ReactNode;
  message: string;
  hint?: string;
  action?: ReactNode;
  className?: string;
}

export function DashedEmpty({ icon, message, hint, action, className = '' }: DashedEmptyProps) {
  return (
    <div className={`flex flex-col items-center justify-center border-2 border-dashed border-line rounded-lg py-8 ${className}`}>
      <div className="text-icon-muted mb-1.5">{icon}</div>
      <p className="text-xs text-muted">{message}</p>
      {hint && <p className="text-[9px] text-subtle mt-0.5">{hint}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

/* ─── MetricCard ───────────────────────────────────────────────
 * KPI card with left-border accent, monospace value, uppercase label.
 * Used in metrics bars and stat grids.
 *
 *   <MetricCard label="Epics" value={12} accent="coral" />
 * ───────────────────────────────────────────────────────────── */

type MetricAccent = 'coral' | 'accent' | 'red' | 'amber' | 'neutral';

const metricAccentMap: Record<MetricAccent, { border: string; value: string; bg: string }> = {
  coral: { border: 'border-l-coral-500', value: 'text-coral-600', bg: 'bg-coral-50/30' },
  accent: { border: 'border-l-accent-500', value: 'text-accent-600', bg: 'bg-accent-50/30' },
  red: { border: 'border-l-red-500', value: 'text-red-600', bg: 'bg-red-50/30' },
  amber: { border: 'border-l-amber-500', value: 'text-amber-600', bg: 'bg-amber-50/30' },
  neutral: { border: 'border-l-neutral-400', value: 'text-text', bg: '' },
};

interface MetricCardProps {
  label: string;
  value: string | number;
  accent?: MetricAccent;
  className?: string;
}

export function MetricCard({ label, value, accent = 'coral', className = '' }: MetricCardProps) {
  const c = metricAccentMap[accent];
  return (
    <div className={`${c.bg} border border-line/80 border-l-[3px] ${c.border} rounded px-3 py-2 ${className}`}>
      <p className="text-[9px] font-bold uppercase tracking-widest text-subtle">{label}</p>
      <p className={`text-lg font-bold font-mono tabular-nums ${c.value}`}>{value}</p>
    </div>
  );
}
